import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  Typography,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NavigationIcon from '@mui/icons-material/Navigation';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DescriptionIcon from '@mui/icons-material/Description';

import { useLazyGetProductsQuery } from '../../store/api/productsApi';
import { useLazyGetCustomersQuery, useLazyGetSalesOrdersQuery } from '../../store/api/salesApi';
import { useNavigate } from 'react-router-dom';

const QUICK_PAGES = [
  { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, category: 'Pages', desc: 'Main business overview' },
  { label: 'POS / New Sales Invoice', path: '/sales/orders/new', icon: PointOfSaleIcon, category: 'Sales', desc: 'Create cash or credit sales (F2)' },
  { label: 'Sales Orders List', path: '/sales', icon: ShoppingCartIcon, category: 'Sales', desc: 'All customer sales & invoices' },
  { label: 'Quotations', path: '/quotations', icon: DescriptionIcon, category: 'Sales', desc: 'Price estimates & quotes' },
  { label: 'Customers', path: '/customers', icon: PeopleIcon, category: 'Sales', desc: 'Customer directory & balances' },
  { label: 'Products & Catalog', path: '/products', icon: InventoryIcon, category: 'Inventory', desc: 'Item list, barcodes & stock' },
  { label: 'Inventory Stock Levels', path: '/inventory', icon: InventoryIcon, category: 'Inventory', desc: 'Warehouses & stock movements' },
  { label: 'Purchase Orders', path: '/purchases', icon: LocalShippingIcon, category: 'Purchases', desc: 'Supplier orders & LPOs' },
  { label: 'Expenses', path: '/expenses', icon: ReceiptLongIcon, category: 'Finance', desc: 'Business expenses & bills' },
  { label: 'Cash & Bank Operations', path: '/finance/cash-bank-operations', icon: AccountBalanceIcon, category: 'Finance', desc: 'Deposits, withdrawals, transfers' },
  { label: 'Financial Reports', path: '/reports', icon: AssessmentIcon, category: 'Reports', desc: 'P&L, Day Book, Sales summaries' },
  { label: 'Company Settings', path: '/settings', icon: SettingsIcon, category: 'Settings', desc: 'System settings & invoice design' },
];

export function GlobalSearchDialog({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all'); // 'all' | 'products' | 'customers' | 'invoices' | 'pages'
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [triggerProducts, { data: productsData, isFetching: loadingProducts }] = useLazyGetProductsQuery();
  const [triggerCustomers, { data: customersData, isFetching: loadingCustomers }] = useLazyGetCustomersQuery();
  const [triggerInvoices, { data: invoicesData, isFetching: loadingInvoices }] = useLazyGetSalesOrdersQuery();

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !query.trim()) return;
    const timer = setTimeout(() => {
      triggerProducts({ search: query.trim(), limit: 8 });
      triggerCustomers({ search: query.trim(), limit: 8 });
      triggerInvoices({ search: query.trim(), limit: 8 });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, triggerProducts, triggerCustomers, triggerInvoices]);

  const rawProducts = useMemo(() => {
    if (!productsData) return [];
    return Array.isArray(productsData) ? productsData : productsData?.products || productsData?.data || [];
  }, [productsData]);

  const rawCustomers = useMemo(() => {
    if (!customersData) return [];
    return Array.isArray(customersData) ? customersData : customersData?.customers || customersData?.data || [];
  }, [customersData]);

  const rawInvoices = useMemo(() => {
    if (!invoicesData) return [];
    return Array.isArray(invoicesData) ? invoicesData : invoicesData?.orders || invoicesData?.data || [];
  }, [invoicesData]);

  const filteredPages = useMemo(() => {
    if (!query.trim()) return QUICK_PAGES;
    const q = query.toLowerCase();
    return QUICK_PAGES.filter(
      (p) => p.label.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
    );
  }, [query]);

  const handleSelect = (path) => {
    onClose();
    navigate(path);
  };

  const isLoading = loadingProducts || loadingCustomers || loadingInvoices;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          bgcolor: '#ffffff',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SearchIcon sx={{ color: '#0ea5e9', fontSize: 28 }} />
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          placeholder="Search products, customers, invoices, or jump to pages... (Cmd + K)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '1.1rem', fontWeight: 500 },
          }}
        />
        {isLoading && <CircularProgress size={20} sx={{ color: '#0ea5e9' }} />}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 0.5, fontSize: '0.85rem', textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab value="all" label="All Results" />
          <Tab value="pages" label={`Pages (${filteredPages.length})`} />
          <Tab value="products" label={`Products (${rawProducts.length})`} />
          <Tab value="customers" label={`Customers (${rawCustomers.length})`} />
          <Tab value="invoices" label={`Invoices (${rawInvoices.length})`} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 480, overflowY: 'auto' }}>
        <List sx={{ py: 0.5 }}>
          {/* Pages */}
          {(tab === 'all' || tab === 'pages') && filteredPages.length > 0 && (
            <Box>
              <Typography sx={{ px: 2, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Navigation & Shortcuts
              </Typography>
              {filteredPages.slice(0, tab === 'all' ? 4 : 12).map((page) => {
                const IconComponent = page.icon;
                return (
                  <ListItem key={page.path} disablePadding>
                    <ListItemButton onClick={() => handleSelect(page.path)} sx={{ px: 2, py: 1, '&:hover': { bgcolor: '#f0f9ff' } }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <IconComponent sx={{ color: '#0ea5e9', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{page.label}</Typography>}
                        secondary={<Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{page.desc}</Typography>}
                      />
                      <Chip label={page.category} size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {tab === 'all' && <Divider sx={{ my: 1 }} />}
            </Box>
          )}

          {/* Products */}
          {(tab === 'all' || tab === 'products') && rawProducts.length > 0 && (
            <Box>
              <Typography sx={{ px: 2, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Products ({rawProducts.length})
              </Typography>
              {rawProducts.slice(0, tab === 'all' ? 4 : 10).map((p) => (
                <ListItem key={p.id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(`/products/${p.id}`)} sx={{ px: 2, py: 1, '&:hover': { bgcolor: '#f0fdf4' } }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <InventoryIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{p.name}</Typography>}
                      secondary={
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                          SKU: {p.sku || '-'} • Price: {Number(p.retailPrice || p.price || 0).toFixed(2)}
                        </Typography>
                      }
                    />
                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                  </ListItemButton>
                </ListItem>
              ))}
              {tab === 'all' && <Divider sx={{ my: 1 }} />}
            </Box>
          )}

          {/* Customers */}
          {(tab === 'all' || tab === 'customers') && rawCustomers.length > 0 && (
            <Box>
              <Typography sx={{ px: 2, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Customers ({rawCustomers.length})
              </Typography>
              {rawCustomers.slice(0, tab === 'all' ? 4 : 10).map((c) => (
                <ListItem key={c.id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(`/customers/${c.id}`)} sx={{ px: 2, py: 1, '&:hover': { bgcolor: '#fdf4ff' } }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PeopleIcon sx={{ color: '#a855f7', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{c.name}</Typography>}
                      secondary={
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                          Phone: {c.phone || '-'} • Email: {c.email || '-'}
                        </Typography>
                      }
                    />
                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                  </ListItemButton>
                </ListItem>
              ))}
              {tab === 'all' && <Divider sx={{ my: 1 }} />}
            </Box>
          )}

          {/* Invoices */}
          {(tab === 'all' || tab === 'invoices') && rawInvoices.length > 0 && (
            <Box>
              <Typography sx={{ px: 2, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Sales Invoices ({rawInvoices.length})
              </Typography>
              {rawInvoices.slice(0, tab === 'all' ? 4 : 10).map((inv) => (
                <ListItem key={inv.id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(`/sales/orders/${inv.id}`)} sx={{ px: 2, py: 1, '&:hover': { bgcolor: '#fffbeb' } }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ReceiptIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                          {inv.invoiceNumber || inv.orderNumber} • {inv.customer?.name || 'Walk-in'}
                        </Typography>
                      }
                      secondary={
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                          Amount: {Number(inv.totalAmount || 0).toFixed(2)} • Status: {inv.status}
                        </Typography>
                      }
                    />
                    <Chip
                      label={inv.status || 'delivered'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20, bgcolor: inv.status === 'delivered' ? '#dcfce7' : '#fee2e2', color: inv.status === 'delivered' ? '#15803d' : '#b91c1c' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Box>
          )}

          {query.trim() && !isLoading && rawProducts.length === 0 && rawCustomers.length === 0 && rawInvoices.length === 0 && filteredPages.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>No results found for "{query}"</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>Try searching for a product name, SKU, customer name, phone number, or invoice number.</Typography>
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}
